import React, { useEffect, useState } from 'react';
import {
  ArrayInput,
  AutocompleteArrayInput,
  BooleanInput,
  Create,
  DateInput,
  FormDataConsumer,
  FormTab,
  ImageField,
  ImageInput,
  NumberInput,
  ReferenceArrayInput,
  ReferenceInput,
  SaveButton,
  SelectArrayInput,
  SelectInput,
  SimpleFormIterator,
  TabbedForm,
  TextField,
  TextInput,
  Toolbar,
  minValue,
  required,
  useDataProvider,
  useTranslate,
} from 'react-admin';

import Button from '@mui/material/Button';

import { Alert, Box } from '@mui/material';
import { Link } from 'react-router-dom';
import { CheckChainUsesModule, DiscountPercentageInput, ImageInformations, LazyLoadAutoCompleteInput, ModuleKey, PriceInput, Tooltip } from '../../components';
import Permission from '../../components/Permission';
import { MAX_DATE, PERMISSIONS } from '../../constants';
import { getChainId } from '../../lib';
import { validatePlaces } from '../../lib/productPlaces';
import { IntegrationType, ProductCostValueType, TagType } from '../../models';
import { SislavProductFields, sislavPlaceFields } from '../../components/SislavFields';
import { dateInputFormatter, filterToQuery } from '../../utils';
import { Grid } from '@mui/material';
import dayjs from 'dayjs';

// Style constants for sx props
const sxStyles = {
  arrayInput: {
    '& p': {
      color: 'grey',
      marginRight: '1em',
      fontWeight: 'bold',
    },
  },
  placeName: {
    padding: '1em 0 0.5em 0',
    width: '100%',
  },
  stockSwitch: {
    margin: '2em 2em 0em 1em'
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 1,
    paddingTop: 1,
  }
};

const create = ({ ...props }) => {
  const [opts, setOpts] = useState([]);
  const [fuels, setFuels] = useState([]);
  const translate = useTranslate();

  const dataProvider = useDataProvider();

  useEffect(() => {
    dataProvider.getList(`chains/${getChainId()}/places`, { filter: {}, sort: { field: 'name', order: 'ASC' } })
      .then(({ data }) => {
        setOpts(data);
      });
    dataProvider.getList('fuels', { filter: {}, sort: { field: 'name', order: 'ASC' } })
      .then(({ data }) => {
        setFuels(data);
      });
  }, [])

  const [addAllPlaces, setAddAllPlaces] = React.useState(false);

  const customAction = () => {
    setAddAllPlaces(true);
  }

  const validate = [required()]
  const optionRenderer = choice => `${choice.code} - ${choice.name}`

  const CreateToolbar = (props) => {
    return (
      <Toolbar {...props}>
        <SaveButton />
      </Toolbar>
    );
  }

  const transform = data => {

    // Remove productBundles se estiver vazio (backend exige pelo menos 1 item ou nenhum)
    if (data.productBundles && data.productBundles.length > 0) {
      data.productBundles = data.productBundles.map(bundle => {
        if (bundle.saleProductIds) {
          bundle.saleProductIds = bundle.saleProductIds.map(p => p && p.saleProductId);
        }
        return bundle;
      });
    } else {
      delete data.productBundles;
    }

    // Linha nula do ArrayInput não pode virar {} (sem posto) no payload.
    data.places = data.places.filter(Boolean).map(place => {
      const placeOpt = opts.find((p) => place.placeId === p.id);
      return {
        ...place,
        isEvoActive: placeOpt?.integration === IntegrationType.evo ? place.isEvoActive : false,
        rtiCode: placeOpt?.integration === IntegrationType.rti ? place.rtiCode : null,
        trevoServiceId: placeOpt?.integration === IntegrationType.trevo ? (place.trevoServiceId ?? null) : null,
        ...sislavPlaceFields(placeOpt?.integration === IntegrationType.sislav, place),
      };
    })
    return data;
  };

  return (
    <Permission permission={PERMISSIONS.CREATE_PRODUCT}>
      <Create
        {...props}
        title="Cadastrar novo produto"
        transform={transform}
      >
        <TabbedForm toolbar={<CreateToolbar />} sanitizeEmptyValues>
          <FormTab label="Produto">
            <Grid container spacing={2} sx={{ maxWidth: 800 }}>
              <Grid size={12}>
                <TextInput source="name" label="Nome" validate={validate} InputProps={{ autoComplete: 'off' }} fullWidth />
              </Grid>
              <Grid size={12}>
                <TextInput
                  source="description"
                  label="Descrição"
                  validate={validate}
                  InputProps={{ autoComplete: 'off' }}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <ReferenceInput
                  source="categoryId"
                  reference="chains/chainId/product-categories"
                  perPage={null}
                  sort={{ field: 'name', order: 'ASC' }}>
                  <SelectInput
                    label="Categoria"
                    optionText="name"
                    emptyText="Nenhuma"
                    fullWidth
                  />
                </ReferenceInput>
              </Grid>
              <Grid size={{ xs: 12, md: 8 }}>
                <ReferenceArrayInput
                  sort={{ field: 'name', order: 'ASC' }}
                  filter={{ type: TagType.product }}
                  perPage={null}
                  source="tagIds"
                  reference="chains/chainId/tag"
                >
                  <SelectArrayInput
                    label="Etiquetas de produto"
                    optionText="name"
                    fullWidth
                  />
                </ReferenceArrayInput>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Tooltip title={translate("tooltips.hiddenProductIds")} placement="top-start" sxContainer={{ alignItems: 'flex-start' }}>
                  <ReferenceArrayInput
                    source="hiddenProductIds"
                    reference="chains/chainId/products"
                    sort={{ field: "name", order: "ASC"}}
                  >
                    <AutocompleteArrayInput
                      label="Produtos ocultos"
                      optionText="name"
                      fullWidth
                      filterToQuery={filterToQuery}
                    />
                  </ReferenceArrayInput>
                </Tooltip>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Tooltip title={translate("tooltips.productCustomerTags")} placement="top-start" sxContainer={{ alignItems: 'flex-start' }}  >
                  <ReferenceArrayInput
                    source="customerTagIds"
                    sort={{ field: 'name', order: 'ASC' }}
                    reference="chains/chainId/tag"
                    filter={{ type: TagType.client }}
                    perPage={null}
                  >
                    <SelectArrayInput
                      label="Etiqueta de clientes"
                      optionText="name"
                      fullWidth
                    />
                  </ReferenceArrayInput>
                </Tooltip>
              </Grid>
              <Grid size={12}>
                <ImageInput source="imagefile" label="Imagem" accept={{ 'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'] }}>
                  <ImageField source="src" label="Imagem" />
                </ImageInput>
                <ImageInformations />
              </Grid>
            </Grid>
          </FormTab>
          <FormTab label="Resgate">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
              <NumberInput
                source="defaultPrice"
                label="Pontos"
                id="defaultPriceInput"
                InputProps={{ autoComplete: 'off' }}
                sx={{ width: 130 }}
              />
              <PriceInput
                source="defaultPartnerPrice"
                label="Valor"
                InputProps={{ autoComplete: 'off' }}
                sx={{ width: 130 }}
              />
              <DiscountPercentageInput
                source="defaultDiscountValue"
                label="Valor do desconto(%)"
                InputProps={{ autoComplete: 'off' }}
              />
              <NumberInput
                source="defaultLimit"
                label="Resgates por dia"
                min={1}
                validate={minValue(1)}
                InputProps={{ autoComplete: 'off' }}
                sx={{ width: 130 }}
              />
              <FormDataConsumer>
                {({ formData }) => {
                  const disabled = formData.places ? formData.places.length === opts.length : false;
                  return (
                    <Button color="primary" disabled={disabled || !formData.defaultPrice} onClick={customAction}>Adicionar Restantes</Button>
                  )
                }}
              </FormDataConsumer>
            </Box>
            <FormDataConsumer>
              {({ formData }) => {
                if (addAllPlaces) {
                  let places;
                  if (formData.places) {
                    const formDataPlaces = formData.places.filter((p) => p);
                    const insertedIds = formDataPlaces.map((p) => p.placeId)
                    const bulkPlaces = opts.map(({ id, name }) => ({
                      price: formData.defaultPrice,
                      partnerPrice: formData.defaultPartnerPrice,
                      discountValue: formData.defaultDiscountValue,
                      limit: formData.defaultLimit,
                      placeId: id,
                      name
                    }));
                    places = formDataPlaces.concat(bulkPlaces.filter(({ placeId }) => !insertedIds.includes(placeId)));
                  } else {
                    places = opts.map(({ id, name }) => ({
                      price: formData.defaultPrice,
                      partnerPrice: formData.defaultPartnerPrice,
                      discountValue: formData.defaultDiscountValue,
                      limit: formData.defaultLimit,
                      placeId: id,
                      name
                    }));
                  }
                  formData.places = places;
                  const defaultPriceInputRef = document['getElementById'](
                    'defaultPriceInput',
                  )
                  setAddAllPlaces(false);
                  defaultPriceInputRef.focus()
                }
                return (
                  <FormDataConsumer>
                    {({ formData: upperFormData, ...upperRest }) => {
                      const disableRemove = upperFormData.places
                        ? upperFormData.places.length === 1
                        : false
                      const disableAdd = upperFormData.places ? upperFormData.places.length === opts.length : false;
                      return (
                        <ArrayInput
                          validate={validatePlaces}
                          label=""
                          source="places"
                          sx={sxStyles.arrayInput}
                          style={{ marginRight: 10 }}
                        >
                          <SimpleFormIterator
                            disableReordering
                            disableRemove={disableRemove}
                            disableAdd={disableAdd}
                            fullWidth
                            {...upperRest}
                          >
                            <FormDataConsumer>
                              {({ formData, scopedFormData }) => {
                                const selectedPlacesIds = formData.places ? formData.places.filter((p) => p).map(
                                  p => p.placeId,
                                ) : [];

                                const availablePlaces = opts.filter(
                                  ({ id }) => !selectedPlacesIds.includes(id) ||
                                    (scopedFormData ? scopedFormData.placeId === id : false),
                                );
                                return (
                                  <>
                                    <Box sx={sxStyles.row}>
                                      {formData.places && (
                                        <SelectInput
                                          source="placeId"
                                          choices={availablePlaces}
                                          label="Ponto de troca"
                                          emptyText="ra.action.clear_input_value"
                                          validate={validate}
                                          sx={{ width: 200 }}
                                        />
                                      )}
                                      <CheckChainUsesModule module={ModuleKey.DOTZ}>
                                        <SelectInput
                                          source="costValueType"
                                          label="Forma de troca"
                                          choices={ProductCostValueType.productCostValueTypeChoices}
                                          defaultValue={ProductCostValueType.points}
                                          sx={{ width: 150 }}
                                        />
                                      </CheckChainUsesModule>
                                      <TextInput
                                        source="costValueType"
                                        defaultValue={ProductCostValueType.points}
                                        sx={{ display: 'none' }}
                                      />
                                      {scopedFormData && scopedFormData.costValueType === ProductCostValueType.dotz ? (
                                        <PriceInput
                                          sx={{ width: 120 }}
                                          source="costValue"
                                          label="Valor Dotz"
                                          validate={[required(), minValue(0)]}
                                        />
                                      ) : (
                                        <NumberInput
                                          source="price"
                                          label="Valor em pontos"
                                          validate={[required(), minValue(0)]}
                                          min={0}
                                          sx={{ width: 130 }}
                                        />
                                      )}
                                      <PriceInput
                                        source="partnerPrice"
                                        label="Valor em reais (Parceiro)"
                                        sx={{ width: 180 }}
                                      />
                                      <DiscountPercentageInput />
                                      <NumberInput
                                        source="limit"
                                        label="Resgates por dia"
                                        min={1}
                                        validate={minValue(1)}
                                        sx={{ width: 130 }}
                                      />
                                      <FormDataConsumer>
                                        {() => {
                                          if (scopedFormData && opts.find((place) => place.id === scopedFormData.placeId && place.integration === IntegrationType.rti)) {
                                            scopedFormData.isEvoActive = false;
                                            scopedFormData.trevoServiceId = null;
                                            return (
                                              <NumberInput
                                                source="rtiCode"
                                                label="Código RTI"
                                                sx={{ width: 120 }}
                                                validate={[minValue(0)]}
                                                min={0}
                                              />
                                            );
                                          } else if (scopedFormData && opts.find((place) => place.id === scopedFormData.placeId && place.integration === IntegrationType.evo)) {
                                            scopedFormData.rtiCode = null;
                                            scopedFormData.trevoServiceId = null;
                                            return (
                                              <Tooltip placement="top-start" sxContainer={{ alignItems: 'flex-start' }} title={translate("tooltips.isEvoActive")}>
                                                <BooleanInput
                                                  label="Integrar com EVO"
                                                  source="isEvoActive"
                                                  sx={{ alignSelf: "center" }}
                                                />
                                              </Tooltip>
                                            )
                                          } else if (scopedFormData && opts.find((place) => place.id === scopedFormData.placeId && place.integration === IntegrationType.trevo)) {
                                            scopedFormData.rtiCode = null;
                                            scopedFormData.isEvoActive = false;
                                            return (
                                              <NumberInput
                                                source="trevoServiceId"
                                                label="Código Serviço Trevo"
                                                sx={{ width: 160 }}
                                                validate={[minValue(0)]}
                                                min={0}
                                              />
                                            );
                                          } else if (scopedFormData && opts.find((place) => place.id === scopedFormData.placeId && place.integration === IntegrationType.sislav)) {
                                            scopedFormData.rtiCode = null;
                                            scopedFormData.isEvoActive = false;
                                            scopedFormData.trevoServiceId = null;
                                            return <SislavProductFields />;
                                          } else if (scopedFormData && opts.length) {
                                            scopedFormData.rtiCode = null;
                                            scopedFormData.isEvoActive = false;
                                            scopedFormData.trevoServiceId = null;
                                          }
                                        }}
                                      </FormDataConsumer>
                                      <FormDataConsumer>
                                        {() => {
                                          if (scopedFormData && opts.find((place) => place.id === scopedFormData.placeId && place.useSanvitronIntegration)) {
                                            return (
                                              <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} title={translate("tooltips.productPlaceIsSanvitron")} placement="top-start">
                                                <BooleanInput
                                                  label="Integrar com Sanvitron"
                                                  source="isSanvitron"
                                                  sx={{ alignSelf: "center" }}
                                                  disabled={scopedFormData.isEvoActive}
                                                />
                                              </Tooltip>
                                            );
                                          } else {
                                            scopedFormData.isSanvitron = false;
                                          }
                                        }}
                                      </FormDataConsumer>
                                    </Box>
                                  </>
                                );
                              }}
                            </FormDataConsumer>
                            <FormDataConsumer>
                              {({ scopedFormData }) => {
                                return scopedFormData && scopedFormData.costValueType !== ProductCostValueType.dotz && (
                                  <BooleanInput
                                    label="Brinde"
                                    source="isGift"
                                    style={{ marginRight: 10 }}
                                  />
                                );
                              }}
                            </FormDataConsumer>
                            <FormDataConsumer>
                              {({ scopedFormData }) => {

                                const disableAddFuel = scopedFormData?.productPlaceGiftFuels ? scopedFormData.productPlaceGiftFuels.length === fuels.length : false

                                return scopedFormData && scopedFormData.isGift &&
                                  <ArrayInput source="productPlaceGiftFuels" label="Combustíveis">
                                    <SimpleFormIterator disableAdd={disableAddFuel} disableReordering>
                                      <FormDataConsumer>
                                        {({ scopedFormData: placeProductScoped }) => {
                                          const selectedfuelIds = scopedFormData.productPlaceGiftFuels ? scopedFormData.productPlaceGiftFuels.filter((p) => p).map(
                                            p => p.fuelId,
                                          ) : [];
                                          const availableFuels = fuels.filter(
                                            ({ id }) => !selectedfuelIds.includes(id) ||
                                              (placeProductScoped ? placeProductScoped.fuelId === id : false),
                                          );

                                          return (
                                            <Box sx={sxStyles.row}>
                                              <NumberInput
                                                label={"Quantidade mínima (litros)"}
                                                source="minimumAmount"
                                                sx={{ width: 180 }}
                                                validate={[required(), minValue(0)]}
                                              />
                                              <SelectInput
                                                label="Combustível"
                                                source="fuelId"
                                                choices={availableFuels}
                                                optionText="name"
                                                validate={validate}
                                                sx={{ width: 160 }}
                                              />
                                              <DateInput label="Data inicial" source="startDate" format={dateInputFormatter} sx={{ width: 150 }} inputProps={{ max: MAX_DATE }} />
                                              <DateInput label="Data final" source="endDate" format={dateInputFormatter} sx={{ width: 150 }} inputProps={{ max: MAX_DATE }} validate={minValue(placeProductScoped.startDate, `Deve ser no mínimo ${dayjs(placeProductScoped.startDate).format("DD/MM/YYYY")}`)} />
                                              <NumberInput
                                                label="Limite de resgate"
                                                source="limitQuantity"
                                                sx={{ width: 130 }}
                                                parse={v => {
                                                  if (v < 0) { return 0; }
                                                  return !v || v === '' ? null : Math.floor(v);
                                                }}
                                              />
                                              <BooleanInput label="Validar em outro local" source="redeemAnywhere" />
                                            </Box>
                                          )
                                        }}
                                      </FormDataConsumer>
                                    </SimpleFormIterator>
                                  </ArrayInput>
                              }}
                            </FormDataConsumer>
                          </SimpleFormIterator>
                        </ArrayInput>
                      )
                    }}
                  </FormDataConsumer>
                )
              }}
            </FormDataConsumer>
          </FormTab>
          <FormTab label="Integração">
            <ArrayInput source="productBundles" label="Ofertas de Produtos">
              <SimpleFormIterator disableReordering>
                <FormDataConsumer>
                  {({ scopedFormData: bundleScopedFormData }) => {
                    return (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
                        <NumberInput
                          source="quantity"
                          label="Quantidade"
                          validate={[required()]}
                          parse={(value) => (isNaN(value) || value <= 0 ? null : Math.round(value))}
                          sx={{ width: 120 }}
                        />
                        <ArrayInput
                          label="Produtos da Oferta"
                          source="saleProductIds"
                          sx={{ ...sxStyles.arrayInput, minWidth: 300 }}
                          validate={[required()]}
                        >
                          <SimpleFormIterator disableReordering>
                            <FormDataConsumer>
                              {({ scopedFormData }) => {
                                const selectedProductIds = bundleScopedFormData?.saleProductIds ? bundleScopedFormData.saleProductIds.filter((p) => p).map(
                                  p => p.saleProductId,
                                ) : [];

                                return (
                                  <LazyLoadAutoCompleteInput
                                    label="Produto de Venda"
                                    source="saleProductId"
                                    format={optionRenderer}
                                    reference="chains/chainId/sale-products"
                                    sx={{ width: 350 }}
                                    validate={[required()]}
                                    choicesFilter={(choices) => {
                                      return choices.filter(choice =>
                                        !selectedProductIds.includes(choice.id) ||
                                        (scopedFormData && scopedFormData.saleProductId === choice.id)
                                      );
                                    }}
                                  />
                                )
                              }}
                            </FormDataConsumer>
                          </SimpleFormIterator>
                        </ArrayInput>
                      </Box>
                    )
                  }}
                </FormDataConsumer>
              </SimpleFormIterator>
            </ArrayInput>
          </FormTab>
          <FormTab label="Estoque">
            <Alert severity="info" sx={{ mb: 1, mt: 2, maxWidth: 800 }}>
              A entrada e baixa de produtos no estoque devem ser realizadas nas respectivas telas:{' '}
              <Link to="/warehouse/product-entry">Entrada de produto</Link> e{' '}
              <Link to="/warehouse/product-write-off">Baixa de produto</Link>.
            </Alert>
            <BooleanInput
              label="Controlar estoque"
              source="stock"
              sx={sxStyles.stockSwitch}
            />
            <FormDataConsumer>
              {({
                formData,
              }) => {
                if (formData.stock) {
                  return (
                    <ArrayInput
                      source="places"
                      label=""
                      sx={sxStyles.arrayInput}>
                      <SimpleFormIterator
                        disableReordering
                        disableRemove
                        disableAdd
                      >
                        <FormDataConsumer>
                          {({
                            scopedFormData,
                          }) => {
                            if (scopedFormData && scopedFormData.placeId) {
                              if (scopedFormData.place) {
                                scopedFormData.name = scopedFormData.place.name
                              } else {
                                const place = opts.find((p) => p.id === scopedFormData.placeId);
                                if (place) {
                                  scopedFormData.name = place.name
                                }
                              }

                              return (
                                <>
                                  <TextField
                                    source={'name'}
                                    record={scopedFormData}
                                    component="p"
                                    variant='subtitle1'
                                    sx={sxStyles.placeName}
                                  />
                                </>
                              )
                            } else {
                              return null;
                            }
                          }
                          }
                        </FormDataConsumer>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          <SelectInput
                            source={'defaultStock'}
                            choices={[
                              { id: 'showcase', name: 'Mostruário' },
                              { id: 'warehouse', name: 'Depósito' },
                            ]}
                            label="Estoque padrão"
                            validate={validate}
                            sx={{ width: 150 }}
                          />
                        </Box>
                        <Tooltip title="Este campo é utilizado apenas para o relatório de sugestão de compra." placement="top-start">
                          <NumberInput
                            source='minimumStock'
                            label='Estoque mínimo'
                            min={0}
                            sx={{ width: 160 }}
                          />
                        </Tooltip>
                      </SimpleFormIterator>
                    </ArrayInput>
                  )
                } else {
                  return null;
                }
              }
              }
            </FormDataConsumer>
          </FormTab>
        </TabbedForm>
      </Create>
    </Permission>
  )
}

export default create
